import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import { Loader, Plus } from 'lucide-react';
import type { RateActionExtractRequestSummary } from './api/rate-action-api';

interface LetterSwitcherProps {
  letters: RateActionExtractRequestSummary[];
  activeRequestId: string | null;
  onSelect: (requestId: string) => void;
  onUploadNew: () => void;
}

export function LetterSwitcher({
  letters,
  activeRequestId,
  onSelect,
  onUploadNew,
}: LetterSwitcherProps) {
  return (
    <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600">Viewing:</span>
        <Select
          value={activeRequestId ?? ''}
          onValueChange={v => v && onSelect(v)}
        >
          <SelectTrigger className="w-full min-w-[16rem] sm:w-auto">
            <SelectValue placeholder="See previously uploaded" />
          </SelectTrigger>
          <SelectContent>
            {letters.map(l => (
              <SelectItem key={l.requestId} value={l.requestId}>
                <span className="flex items-center gap-2">
                  <StatusDot status={l.status} />
                  <span>{formatLetterLabel(l)}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {activeRequestId && (
        <Button variant="outline" size="sm" onClick={onUploadNew}>
          <Plus className="mr-1 size-4" /> Upload new
        </Button>
      )}
    </div>
  );
}

function StatusDot({ status }: { status: RateActionExtractRequestSummary['status'] }) {
  if (status === 'processing') {
    return <Loader aria-label="Processing" className="size-3 animate-spin text-yellow-700" />;
  }
  const cls =
    status === 'applied'
      ? 'bg-primary'
      : status === 'completed'
        ? 'bg-green-600'
        : 'bg-red-600';
  return <span aria-label={status} className={`inline-block size-2 rounded-full ${cls}`} />;
}

function formatLetterLabel(l: RateActionExtractRequestSummary): string {
  const parts: string[] = [];
  parts.push(formatDate(l.createdDateTime));
  if (l.carrier) parts.push(l.carrier);
  if (l.percentageIncrease != null) {
    const sign = l.percentageIncrease > 0 ? '+' : '';
    parts.push(`${sign}${l.percentageIncrease.toFixed(1)}%`);
  }
  return parts.join(' · ');
}

function formatDate(iso: string | null): string {
  if (!iso) return 'Untitled';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Untitled';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
